import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { unitLabel } from '@d-orca/shared/client/logistics';
import { zonedDay, formatDay } from '../logistics.format';
import type { IDeliveryOrderDialogProps } from './deliveryOrder.types';
import styles from './DeliveryOrderForm.module.css';

interface IDeliveryOrderMergeBoxProps {
  /** Guaranteed non-null by the caller: this box only renders while
   *  `canMerge` holds, which already implies an open stock row. */
  openStock: NonNullable<IDeliveryOrderDialogProps['openStock']>;
  tz: string;
  mergeStock: boolean;
  setMergeStock: (next: boolean) => void;
  mergeRemaining: string;
  setMergeRemaining: (next: string) => void;
  seededCls: (field: string, value: string) => string;
  seedNote: (field: string, value: string, numeric?: boolean) => ReactNode;
  qtyNum: number;
  remValid: boolean;
  remNum: number;
  mergedTotal: number | null;
}

/** Marking a delivery arrived means both amounts are now on site. Offering the
 *  merge here saves the trip to the inventory row — recording the arrival and
 *  reconciling the stock are one motion. Opt-in, because they are two
 *  different claims: the arrival is a fact, what is left on the floor is a
 *  count nobody has taken yet. */
export function DeliveryOrderMergeBox({
  openStock, tz, mergeStock, setMergeStock, mergeRemaining, setMergeRemaining,
  seededCls, seedNote, qtyNum, remValid, remNum, mergedTotal,
}: IDeliveryOrderMergeBoxProps) {
  const { t, i18n } = useTranslation();
  // The stock row's stored unit (a key, or a legacy label) in the viewer's words.
  const unit = unitLabel(t, openStock.unit);
  return (
    <div className={styles.mergeBox}>
      <label className={styles.mergeToggle}>
        <input
          type="checkbox"
          checked={mergeStock}
          onChange={(e) => setMergeStock(e.target.checked)}
        />
        <span>{t('logistics.orderDialog.mergeAsk', { unit })}</span>
      </label>
      {/* The one fact the system actually has, handed to the person
          deciding: WHEN the running stock was last counted. It cannot
          answer "does that count already include this" — a plain
          recount never touches a delivery record, so an arrival that
          physically preceded its own board move leaves no trace here
          to check against. Rather than guess (and risk a confident,
          wrong warning), the date is stated and the call stays the
          user's — the same reasoning that makes the whole box opt-in. */}
      {openStock.enteredAt ? (
        <span className={styles.mergeHint}>
          {t('logistics.orderDialog.mergeCountHint', {
            date: formatDay(zonedDay(openStock.enteredAt, tz), i18n.language),
          })}
        </span>
      ) : null}
      {mergeStock ? (
        <>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="merge-remaining">
              {t('logistics.stockDialog.remaining')}
            </label>
            <div className={styles.rowFields}>
              <input
                id="merge-remaining"
                className={`${styles.input}${seededCls('mergeRemaining', mergeRemaining)}`}
                value={mergeRemaining}
                onChange={(e) => setMergeRemaining(e.target.value)}
                inputMode="decimal"
                autoComplete="off"
              />
              <span className={`${styles.input} ${styles.lockedValue}`}>{unit}</span>
            </div>
            {/* Left uncorrected this is the estimate restated, and the
                cycle it closes teaches the system what it already knew. */}
            <span className={styles.mergeHint}>
              {t('logistics.stockDialog.remainingWhy')}
              <span className={styles.fieldAsk}>{t('logistics.stockDialog.remainingCorrect')}</span>
            </span>
            {seedNote('mergeRemaining', mergeRemaining, true)}
          </div>
          <span className={styles.mergeTotal}>
            {t('logistics.orderDialog.mergeTotal', {
              total: mergedTotal ?? '—',
              unit,
              left: remValid ? remNum : 0,
              arrived: qtyNum,
            })}
          </span>
        </>
      ) : null}
    </div>
  );
}
